import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PackageSearch, ScanBarcode } from 'lucide-react';
import { api, errorMessage } from '@/lib/api/client';
import { cn } from '@/lib/cn';
import {
  Detail,
  DetailList,
  EmptyState,
  ErrorState,
  Page,
  PageHeader,
  Panel,
  Skeleton,
  Toolbar,
} from '@/ui/Panel';
import { SplitView } from '@/ui/SplitView';
import { SearchInput } from '@/ui/Field';
import { Badge, Code, type Tone } from '@/ui/Badge';
import { useShortcutHint } from '@/app/StatusLine';
import { marginPct, money, num, percent, qty } from '@/lib/format';
import { asOptionalText, asText } from '@/lib/searchParams';

/**
 * Lookup — "what is this thing and what does it cost".
 *
 * The fastest screen in the console and the one an operator hits mid-conversation
 * with a customer standing there. Everything is subordinate to that: the field
 * autofocuses, results stay on screen while the next query is in flight
 * (`placeholderData`), and the whole thing is drivable without the mouse.
 *
 * A digits-only query of six or more characters is treated as a scan and also
 * goes to `GET /barcodes?codes=`, which bypasses pagination and returns an exact
 * match. The catalog search runs regardless — a scanner that mis-reads one digit
 * should still surface the neighbouring product rather than an empty screen.
 */

/* ── Wire shapes (verified against server/api/src/modules/catalog/routes.ts) ── */

/** `GET /products` row. Note `id` is a NUMBER — products.id is a serial. */
type ProductRow = {
  id: number;
  name: string;
  legacyId: string | null;
  price: number;
  cost: number | null;
  inventoryTracking: string;
  stock: number | null;
  ebtEligible: boolean;
  wicEligible: boolean;
  soldByWeight: boolean;
  department: string | null;
  brand: string | null;
  zoneName: string | null;
  shelfLocation: string | null;
  status: string;
  /** TASK-0259: an input product, bought for the butcher or kitchen, never sold. */
  purchasingOnly: boolean;
};

type ProductListResponse = {
  data: ProductRow[];
  total: number;
  page: number;
  pageSize: number;
};

/** `GET /barcodes?codes=` row. */
type BarcodeRow = {
  id: number;
  productId: number;
  productName: string | null;
  legacyId: string | null;
  code: string;
  type: string | null;
  isPrimary: boolean | null;
  price: number | null;
  stock: number | null;
  inventoryTracking: string;
};

/** `GET /products/:id` — the whole products row plus joins. */
type ProductDetail = {
  id: number;
  name: string;
  receiptName: string | null;
  labelName: string | null;
  legacyId: string | null;
  price: number;
  cost: number | null;
  avgCost: number | null;
  taxable: boolean;
  taxExempt: boolean;
  ebtEligible: boolean;
  wicEligible: boolean;
  soldByWeight: boolean;
  unit: string;
  priceType: string;
  shelfLocation: string | null;
  reorderPoint: number | null;
  reorderQty: number | null;
  storageType: string | null;
  inventoryTracking: string;
  active: boolean;
  caseQty: number | null;
  casePriceCents: number | null;
  casePricingEnabled: boolean;
  sizeValue: string | null;
  sizeUnit: string | null;
  zoneName: string | null;
  brand: string | null;
  stock: number | null;
  reservedQty: number | string;
  purchasingOnly: boolean;
  familyName: string | null;
  barcodes: Array<{
    id: number;
    code: string;
    type: string | null;
    isPrimary: boolean | null;
  }>;
};

/** A row in the result list, whatever it came from. */
type Hit = {
  productId: number;
  name: string;
  price: number | null;
  stock: number | null;
  inventoryTracking: string;
  department: string | null;
  brand: string | null;
  legacyId: string | null;
  /** Set when the row came from an exact barcode match. */
  matchedCode?: string;
  purchasingOnly?: boolean;
};

const PAGE_SIZE = 12;
const BARCODE_RE = /^\d{6,}$/;

/* ── Route ────────────────────────────────────────────────────────────────── */

export const Route = createFileRoute('/_app/lookup')({
  staticData: { crumb: 'Lookup' },
  validateSearch: (s: Record<string, unknown>) => ({
    q: asText(s.q, ''),
    id: asOptionalText(s.id),
  }),
  component: LookupScreen,
});

function LookupScreen() {
  const { q, id } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  // SearchInput is not a forwardRef component, so the field is reached through
  // its wrapper rather than by threading a ref into shared UI.
  const fieldRef = useRef<HTMLDivElement>(null);
  const focusField = () => fieldRef.current?.querySelector('input')?.focus();

  // The field holds keystrokes; the URL holds the committed query. Debouncing
  // between the two is what keeps this screen from firing a request per letter.
  const [draft, setDraft] = useState(q);
  const [cursor, setCursor] = useState(0);

  useShortcutHint([
    { keys: '↑↓', label: 'Move' },
    { keys: '↵', label: 'Select' },
    { keys: 'Esc', label: 'Clear' },
  ]);

  // Back/forward or a deep link changes `q` underneath us; follow it.
  useEffect(() => {
    setDraft((d) => (d === q ? d : q));
  }, [q]);

  useEffect(() => {
    if (draft === q) return;
    const t = window.setTimeout(() => {
      void navigate({
        search: (prev: { q: string; id: string | undefined }) => ({
          ...prev,
          q: draft,
        }),
        replace: true,
      });
    }, 150);
    return () => window.clearTimeout(t);
  }, [draft, q, navigate]);

  const term = q.trim();
  const looksLikeBarcode = BARCODE_RE.test(term);

  const products = useQuery({
    queryKey: ['lookup', 'products', term],
    queryFn: () =>
      api.get<ProductListResponse>('/products', {
        params: { search: term, pageSize: PAGE_SIZE },
      }),
    enabled: term.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const barcode = useQuery({
    queryKey: ['lookup', 'barcode', term],
    queryFn: () => api.get<BarcodeRow[]>('/barcodes', { params: { codes: term } }),
    enabled: term.length > 0 && looksLikeBarcode,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  /* Barcode hits lead — an exact scan match is the answer the operator wants —
     then catalog results, deduped so a scanned item never appears twice. */
  const hits = useMemo<Hit[]>(() => {
    const out: Hit[] = [];
    const seen = new Set<number>();
    for (const b of barcode.data ?? []) {
      if (seen.has(b.productId)) continue;
      seen.add(b.productId);
      out.push({
        productId: b.productId,
        name: b.productName ?? `Product ${b.productId}`,
        price: b.price,
        stock: b.stock,
        inventoryTracking: b.inventoryTracking,
        department: null,
        brand: null,
        legacyId: b.legacyId,
        matchedCode: b.code,
      });
    }
    for (const p of products.data?.data ?? []) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      out.push({
        productId: p.id,
        name: p.name,
        price: p.price,
        stock: p.stock,
        inventoryTracking: p.inventoryTracking,
        department: p.department,
        brand: p.brand,
        legacyId: p.legacyId,
        purchasingOnly: p.purchasingOnly,
      });
    }
    return out;
  }, [barcode.data, products.data]);

  // A new result set puts the cursor back on the first row, which is the one
  // an operator expects Enter to take.
  useEffect(() => {
    setCursor(0);
  }, [term]);

  useEffect(() => {
    if (cursor > hits.length - 1) setCursor(Math.max(0, hits.length - 1));
  }, [hits.length, cursor]);

  const select = (productId: number | undefined) => {
    void navigate({
      search: (prev: { q: string; id: string | undefined }) => ({
        ...prev,
        id: productId === undefined ? undefined : String(productId),
      }),
      replace: true,
    });
  };

  const onKeyDown = (e: ReactKeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, Math.max(0, hits.length - 1)));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
        break;
      case 'Enter': {
        e.preventDefault();
        select(hits[cursor]?.productId);
        break;
      }
      case 'Escape':
        // SearchInput swallows the first Escape to clear its own text; this
        // one lands when the field is already empty.
        e.preventDefault();
        setDraft('');
        select(undefined);
        focusField();
        break;
    }
  };

  // Typing anywhere on the screen returns focus to the field — this screen is
  // the search box, and clicking a result must not cost you the keyboard.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key.length !== 1) return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      ) {
        return;
      }
      fieldRef.current?.querySelector('input')?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const selectedId = id ? Number(id) : null;
  const loading = products.isFetching || barcode.isFetching;

  return (
    <Page>
      <PageHeader
        eyebrow="Overview"
        title="Lookup"
        subtitle="Price check — scan a barcode or type a name"
      />

      <Toolbar className="py-3">
        {/* The wrapper carries the key handling: SearchInput defines its own
            onKeyDown internally, so a prop would be discarded — but React
            events still bubble here. */}
        <div className="w-full" ref={fieldRef} onKeyDown={onKeyDown}>
          <SearchInput
            size="lg"
            autoFocus
            value={draft}
            onValueChange={setDraft}
            placeholder="Scan a barcode, or search by name, brand or PLU…"
            className="font-mono"
            aria-label="Product lookup"
          />
        </div>
      </Toolbar>

      <div className="min-h-0 flex-1">
        <SplitView
          storageKey="lookup"
          onCloseDetail={() => select(undefined)}
          defaultDetail={460}
          list={
            <ResultList
              hits={hits}
              cursor={cursor}
              selectedId={selectedId}
              term={term}
              loading={loading}
              error={products.error ?? barcode.error}
              onHover={setCursor}
              onPick={(pid, index) => {
                setCursor(index);
                select(pid);
              }}
            />
          }
          detail={
            selectedId !== null ? <ProductPanel productId={selectedId} /> : undefined
          }
          placeholder={
            <EmptyState
              icon={<PackageSearch />}
              title="Nothing selected"
              description="Arrow to a result and press Enter to inspect price, cost, margin and stock."
            />
          }
        />
      </div>
    </Page>
  );
}

/* ── Result list ──────────────────────────────────────────────────────────── */

function ResultList({
  hits,
  cursor,
  selectedId,
  term,
  loading,
  error,
  onHover,
  onPick,
}: {
  hits: Hit[];
  cursor: number;
  selectedId: number | null;
  term: string;
  loading: boolean;
  error: unknown;
  onHover: (index: number) => void;
  onPick: (productId: number, index: number) => void;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bodyRef.current
      ?.querySelector<HTMLElement>('[data-cursor="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (error) {
    return (
      <ErrorState title="Lookup failed" message={errorMessage(error)} className="m-3" />
    );
  }

  if (term.length === 0) {
    return (
      <EmptyState
        icon={<ScanBarcode />}
        title="Scan or type to look something up"
        description="Six or more digits are treated as a barcode and matched exactly. Anything else searches names, brands and barcodes."
      />
    );
  }

  if (hits.length === 0 && loading) {
    return (
      <div className="flex flex-col gap-px p-3">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (hits.length === 0) {
    return (
      <EmptyState
        icon={<PackageSearch />}
        title="No match"
        description={`Nothing in the catalog matches “${term}”. Check the digits, or search part of the product name.`}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-col">
      <div
        ref={bodyRef}
        className={cn('scrollpane min-h-0 flex-1', loading && 'opacity-60')}
      >
        {hits.map((hit, i) => {
          const isCursor = i === cursor;
          const isSelected = hit.productId === selectedId;
          return (
            <button
              key={hit.productId}
              type="button"
              data-cursor={isCursor || undefined}
              onMouseEnter={() => onHover(i)}
              onClick={() => onPick(hit.productId, i)}
              className={cn(
                'border-line-soft flex w-full items-center gap-3 border-b px-3 py-1.5 text-left',
                'hover:bg-surface-hover',
                isSelected && 'bg-signal-muted',
                isCursor && 'ring-signal-line ring-1 ring-inset'
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-ink truncate text-sm">{hit.name}</span>
                  {hit.matchedCode ? <Badge tone="signal">Barcode</Badge> : null}
                </span>
                <span className="text-2xs text-ink-faint flex items-center gap-2">
                  {hit.matchedCode ? (
                    <Code>{hit.matchedCode}</Code>
                  ) : hit.legacyId ? (
                    <Code>{hit.legacyId}</Code>
                  ) : null}
                  <span className="truncate">
                    {[hit.brand, hit.department].filter(Boolean).join(' · ')}
                  </span>
                </span>
              </span>
              <span className="shrink-0 text-right">
                {hit.purchasingOnly ? (
                  <Badge tone="info">Input, not sold</Badge>
                ) : (
                  <span className="text-ink block font-mono text-sm tabular-nums">
                    {money(hit.price)}
                  </span>
                )}
                <span className="text-2xs text-ink-faint block font-mono tabular-nums">
                  {stockLabel(hit.stock, hit.inventoryTracking)}
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="border-line bg-surface-sunken text-2xs text-ink-faint flex h-7 shrink-0 items-center gap-3 border-t px-3">
        <span className="font-mono tabular-nums">{num(hits.length)} results</span>
        {loading ? <span className="text-signal-ink">refreshing…</span> : null}
      </div>
    </div>
  );
}

function stockLabel(stock: number | null, tracking: string): string {
  if (tracking === 'untracked' || stock === null) return 'untracked';
  return `${qty(stock)} on hand`;
}

/* ── Selected product ─────────────────────────────────────────────────────── */

function ProductPanel({ productId }: { productId: number }) {
  const detail = useQuery({
    queryKey: ['products', String(productId)],
    queryFn: () => api.get<ProductDetail>(`/products/${productId}`),
    staleTime: 30_000,
  });

  if (detail.isPending) {
    return (
      <div className="flex flex-col gap-3 p-3">
        <Skeleton className="h-6 w-3/4" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (detail.isError) {
    return (
      <ErrorState
        title="Could not load this product"
        message={errorMessage(detail.error)}
        onRetry={() => void detail.refetch()}
        className="m-3"
      />
    );
  }

  const p = detail.data;
  const margin = p.cost === null ? null : marginPct(p.price, p.cost);
  const reserved = Number(p.reservedQty ?? 0);
  const available =
    p.inventoryTracking === 'untracked' || p.stock === null ? null : p.stock - reserved;

  const flags: Array<{ label: string; on: boolean; tone: Tone }> = [
    { label: 'Taxable', on: p.taxable, tone: 'info' },
    { label: 'EBT', on: p.ebtEligible, tone: 'positive' },
    { label: 'WIC', on: p.wicEligible, tone: 'positive' },
    { label: 'By weight', on: p.soldByWeight, tone: 'signal' },
  ];

  return (
    <div className="flex flex-col gap-3 p-3">
      <div>
        <div className="flex items-start gap-2">
          <h2 className="text-ink min-w-0 flex-1 text-base leading-snug font-medium">
            {p.name}
          </h2>
          {!p.active ? <Badge tone="negative">Inactive</Badge> : null}
        </div>
        <div className="text-2xs text-ink-faint mt-1 flex flex-wrap items-center gap-1.5">
          {p.legacyId ? <Code copyable>{p.legacyId}</Code> : null}
          {p.brand ? <span>{p.brand}</span> : null}
          {p.sizeValue ? (
            <span>
              {qty(p.sizeValue)} {p.sizeUnit ?? ''}
            </span>
          ) : null}
        </div>
      </div>

      {/* The headline numbers, big enough to read across a counter. */}
      <div className="grid grid-cols-3 gap-2">
        <PriceTile
          label="Price"
          value={p.purchasingOnly ? 'Input, not sold' : money(p.price)}
        />
        <PriceTile label="Cost" value={money(p.cost)} muted />
        <PriceTile
          label="Margin"
          value={margin === null ? '—' : percent(margin)}
          tone={margin !== null && margin < 0 ? 'negative' : undefined}
          muted
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {flags
          .filter((f) => f.on)
          .map((f) => (
            <Badge key={f.label} tone={f.tone}>
              {f.label}
            </Badge>
          ))}
        {flags.every((f) => !f.on) ? (
          <span className="text-2xs text-ink-faint">
            No tax, EBT, WIC or weight flags set
          </span>
        ) : null}
      </div>

      <Panel title="Stock">
        <DetailList columns={2}>
          <Detail label="On hand" mono>
            {p.inventoryTracking === 'untracked' || p.stock === null
              ? 'Untracked'
              : qty(p.stock)}
          </Detail>
          <Detail label="Reserved" mono>
            {qty(reserved)}
          </Detail>
          <Detail label="Available" mono>
            {available === null ? '—' : qty(available)}
          </Detail>
          <Detail label="Tracking">{p.inventoryTracking}</Detail>
          <Detail label="Reorder point" mono>
            {p.reorderPoint === null ? '—' : num(p.reorderPoint)}
          </Detail>
          <Detail label="Storage">{p.storageType ?? '—'}</Detail>
        </DetailList>
      </Panel>

      <Panel title="Where it lives">
        <DetailList columns={2}>
          <Detail label="Zone">{p.zoneName ?? '—'}</Detail>
          <Detail label="Shelf">{p.shelfLocation ?? '—'}</Detail>
          <Detail label="Receipt name">{p.receiptName ?? '—'}</Detail>
          <Detail label="Label name">{p.labelName ?? '—'}</Detail>
        </DetailList>
      </Panel>

      {p.casePricingEnabled ? (
        <Panel title="Case pricing">
          <DetailList columns={2}>
            <Detail label="Case qty" mono>
              {p.caseQty === null ? '—' : num(p.caseQty)}
            </Detail>
            <Detail label="Case price" mono>
              {money(p.casePriceCents)}
            </Detail>
          </DetailList>
        </Panel>
      ) : null}

      <Panel title={`Barcodes (${p.barcodes.length})`} bleed>
        {p.barcodes.length === 0 ? (
          <div className="text-ink-faint p-3 text-xs">No barcodes on file.</div>
        ) : (
          <ul className="divide-line-soft divide-y">
            {p.barcodes.map((b) => (
              <li key={b.id} className="flex items-center gap-2 px-3 py-1.5">
                <Code copyable className="text-xs">
                  {b.code}
                </Code>
                <span className="text-2xs text-ink-faint">{b.type ?? '—'}</span>
                {b.isPrimary ? <Badge tone="signal">Primary</Badge> : null}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function PriceTile({
  label,
  value,
  muted = false,
  tone,
}: {
  label: string;
  value: string;
  muted?: boolean;
  tone?: 'negative';
}) {
  return (
    <div className="border-line bg-surface-sunken rounded border px-2 py-1.5">
      <div className="text-2xs text-ink-faint tracking-wider uppercase">{label}</div>
      <div
        className={cn(
          'truncate font-mono tabular-nums',
          muted ? 'text-ink-muted text-base' : 'text-ink text-lg',
          tone === 'negative' && 'text-negative'
        )}
      >
        {value}
      </div>
    </div>
  );
}
